import {
  BaseEquipment,
  EnabledFeatures,
  Equipment,
  EquipmentCombinations,
  IEnabledFeatures,
  WorkoutSettingsBase,
} from 'fitbeat.models';
import {
  UserProfile,
} from 'fitbeat.gym-core';
import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Platform, StyleSheet, Text, View,
} from 'react-native';
import { connect } from 'react-redux';
import { Switch } from '../../ui/components/Switch';
import BenchIcon from '../../assets/images/icon-bench-outline.svg';
import BenchAndDumbbellsIcon from '../../assets/images/icon-bench-and-dumbbells-outline.svg';
import BodyWeightIcon from '../../assets/images/icon-bodyweight-outline.svg';
import DumbbellsIcon from '../../assets/images/icon-dumbbells-outline.svg';
import { IAppStore, Orientation, Orientations } from '../../HomeWorkouts/reducer';
import { orientationSelector } from '../../HomeWorkouts/selectors';
import Theme, { ThemeType } from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { themeStyleMapper } from '../steps/personaliseWorkoutSteps';
import { SelectionContainer } from './SelectionContainer';

const isDesktopWeb = Platform.OS === 'web' && window.innerWidth >= 550;

const totalPossibleWidth = 100;
const equipmentOptionCount = 4;
const margin = 2;
const equipmentSelectionContainerWidth = ((totalPossibleWidth / equipmentOptionCount) - margin);

interface IProps {
  t: (str: string, variable?: string) => string;
  sectionHeading: () => JSX.Element;
  orientation: Orientation;
  enabledFeatures: IEnabledFeatures;
  defaultEquipment?: Equipment[];
  equipment: any | null;
  theme: ThemeType;
  workoutSettings: WorkoutSettingsBase,
  userProfile: UserProfile,
  onToggleEquipment(workoutEquipment: any, shouldSaveDefaultEquipment: boolean);
}

const getEquipmentList = (hasDumbells: boolean, hasBands: boolean, hasBenchAndDumbbells: boolean): Equipment[] => {
  if (hasBenchAndDumbbells) {
    return BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_HUB_DUMBBELLS] || [];
  }
  if (hasDumbells && hasBands) {
    return BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_BANDS_DUMBBELLS] || [];
  }
  if (hasDumbells) {
    return BaseEquipment.EquipmentCombinationList[EquipmentCombinations.DUMBBELLS] || [];
  }
  if (hasBands) {
    return BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_BANDS] || [];
  }
  return BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BODYWEIGHT] || [];
};

const EquipmentSelectionComponent: FunctionComponent<IProps> = ({
  theme,
  orientation,
  onToggleEquipment,
  t,
  equipment,
  enabledFeatures,
  defaultEquipment,
  sectionHeading,
}) => {
  const iconHeight = defaultScale(40);
  const iconWidth = defaultScale(40);
  const themeStyle = themeStyleMapper[theme];
  const orientationStyle = orientationStyleMapper[orientation];
  const equipmentCombo = BaseEquipment.getEquipmentCombination(equipment);
  const bodyWeightSelectedByDefault = equipmentCombo === EquipmentCombinations.BODYWEIGHT;
  const dumbbellsSelectedByDefault = BaseEquipment.hasOnlyDumbbells(equipmentCombo);
  const benchAndDumbbellsSelectedByDefault = BaseEquipment.hasBenchAndDumbbellsCombination(equipmentCombo);
  const bandsSelectedByDefault = BaseEquipment.hasBandsCombination(equipmentCombo);

  const defaultEquipmentSettings = EnabledFeatures.calculateEquipmentSettings(enabledFeatures, defaultEquipment);
  const defaultEquipmentCombo = BaseEquipment.getEquipmentCombination(defaultEquipmentSettings);

  const [isBodyWeightSelected, setIsBodyWeightSelected] = useState<boolean>(bodyWeightSelectedByDefault);
  const [isDumbbellsSelected, setIsDumbbellsSelected] = useState<boolean>(dumbbellsSelectedByDefault);
  const [isBenchAndDumbbellsSelected, setIsBenchAndDumbbellsSelected] = useState<boolean>(
    benchAndDumbbellsSelectedByDefault,
  );
  const [isBandsSelected, setIsBandsSelected] = useState<boolean>(bandsSelectedByDefault);
  const [isMakeDefaultSelected, setMakeDefaultSelected] = useState<boolean>(
    defaultEquipmentCombo === equipmentCombo,
  );
  const [equipmentSelected, setEquipmentSelected] = useState<Equipment[]>(
    getEquipmentList(dumbbellsSelectedByDefault, bandsSelectedByDefault, benchAndDumbbellsSelectedByDefault),
  );

  useEffect(() => {
    onToggleEquipment(equipmentSelected, isMakeDefaultSelected);
  }, [isMakeDefaultSelected]);

  const setEquipment = (
    hasDumbells: boolean,
    hasBands: boolean,
    hasBenchAndDumbbells: boolean,
    defaultEquipmentEnabled?: boolean,
  ) => {
    const equipment = getEquipmentList(hasDumbells, hasBands, hasBenchAndDumbbells);
    setEquipmentSelected(equipment);
    onToggleEquipment(equipment, defaultEquipmentEnabled || isMakeDefaultSelected);
    const equipmentCombo = BaseEquipment.getEquipmentCombination(equipment);
    const defaultEquipmentSettings = EnabledFeatures.calculateEquipmentSettings(enabledFeatures, defaultEquipment);
    const defaultEquipmentCombo = BaseEquipment.getEquipmentCombination(defaultEquipmentSettings);
    const defaultSettings = defaultEquipmentEnabled === undefined
      ? defaultEquipmentCombo === equipmentCombo
      : defaultEquipmentEnabled;
    setMakeDefaultSelected(defaultEquipmentEnabled || defaultSettings);
  };

  const toggleEquipment = (
    hasDumbells: boolean,
    hasBands: boolean,
    hasBenchAndDumbbells: boolean,
    defaultEquipmentEnabled?: boolean,
  ) => {
    setIsDumbbellsSelected(hasDumbells);
    setIsBandsSelected(hasBands);
    setIsBenchAndDumbbellsSelected(hasBenchAndDumbbells);
    setIsBodyWeightSelected(!hasDumbells && !hasBands && !hasBenchAndDumbbells);
    setEquipment(hasDumbells, hasBands, hasBenchAndDumbbells, defaultEquipmentEnabled);
  };

  const renderDefaultEquipmentToggle = (containerStyle: any, onToggle: (value: boolean) => void) => (
    <View
      style={containerStyle}
    >
      <Text
        style={[
          Theme.textStyle.bodyCopyMed,
          themeStyle.textColor1,
        ]}
      >
        {t('setDefaultEquipment')}
      </Text>
      <Switch
        onValueChange={onToggle}
        value={isMakeDefaultSelected}
      />
    </View>
  );

  return (
    <View style={{ flex: 1 }}>
      <View style={styles.headingOptionsContainer}>
        {sectionHeading()}
        <View>
          <View
            style={[
              styles.bookingSelectionContainer,
              orientationStyle.bookingSelectionContainerMargin,
            ]}
          >
            <SelectionContainer
              title={t('settings:bodyweightOnly')}
              subTitle={t('settings:noEquipment')}
              titleStyle={styles.bookingSelectionTitle}
              subTitleStyle={styles.bookingSelectionSubTitle}
              icon={(
                <BodyWeightIcon
                  height={iconHeight}
                  width={iconWidth}
                  stroke={isBodyWeightSelected ? themeStyle.themeColor1Inverse : themeStyle.themeColor1}
                />
)}
              theme={theme}
              height={orientationStyle.bookingSelectionContainerHeight}
              width={orientationStyle.bookingSelectionContainerWidth}
              isSelected={isBodyWeightSelected}
              onPress={() => toggleEquipment(false, false, false)}
            />
            <SelectionContainer
              title={t('settings:dumbbells')}
              subTitle={t('settings:useYourOwnSet')}
              titleStyle={styles.bookingSelectionTitle}
              subTitleStyle={styles.bookingSelectionSubTitle}
              icon={(
                <DumbbellsIcon
                  height={iconHeight}
                  width={iconWidth}
                  stroke={isDumbbellsSelected
                    ? themeStyle.themeColor1Inverse
                    : themeStyle.themeColor1}
                />
)}
              theme={theme}
              height={orientationStyle.bookingSelectionContainerHeight}
              width={orientationStyle.bookingSelectionContainerWidth}
              isSelected={isDumbbellsSelected}
              onPress={() => toggleEquipment(true, false, false)}
              disabled={false}
            />
            <SelectionContainer
              title={t('settings:benchAndDumbbells')}
              subTitle={t('settings:benchAndDumbbellsDescription')}
              subTitleStyle={styles.bookingSelectionSubTitle}
              icon={(
                <BenchAndDumbbellsIcon
                  height={iconHeight}
                  width={iconWidth}
                  stroke={isBenchAndDumbbellsSelected
                    ? themeStyle.themeColor1Inverse
                    : themeStyle.themeColor1}
                />
)}
              theme={theme}
              titleStyle={styles.bookingSelectionTitle}
              height={orientationStyle.bookingSelectionContainerHeight}
              width={orientationStyle.bookingSelectionContainerWidth}
              isSelected={isBenchAndDumbbellsSelected}
              onPress={() => toggleEquipment(false, false, true)}
              disabled={false}
            />
            <SelectionContainer
              title={t('settings:smartHomeGym')}
              subTitle={t('settings:fitillionBenchAndBands')}
              subTitleStyle={styles.bookingSelectionSubTitle}
              icon={(
                <BenchIcon
                  height={iconHeight}
                  width={iconWidth}
                  stroke={isBandsSelected
                    ? themeStyle.themeColor1Inverse
                    : themeStyle.themeColor1}
                />
)}
              theme={theme}
              titleStyle={styles.bookingSelectionTitle}
              height={orientationStyle.bookingSelectionContainerHeight}
              width={orientationStyle.bookingSelectionContainerWidth}
              isSelected={isBandsSelected}
              onPress={() => toggleEquipment(false, true, false)}
              disabled={false}
            />
          </View>
        </View>
      </View>
      {renderDefaultEquipmentToggle([
        orientationStyle.iconLabelSwitchContainerLast,
      ], (value) => {
        setMakeDefaultSelected(value);
        toggleEquipment(isDumbbellsSelected, isBandsSelected, isBenchAndDumbbellsSelected, value);
      })}
    </View>
  );
};

const mapStateToProps = (state: IAppStore): Partial<IProps> => ({
  orientation: orientationSelector(state),
});

const orientationStyleMapper = {
  [Orientations.portrait]: {
    bookingSelectionContainerHeight: isDesktopWeb ? defaultScale(110) : defaultScale(115),
    bookingSelectionContainerWidth: defaultScale(340),
    bookingSelectionContainerMargin: {
      height: isDesktopWeb ? defaultScale(460) : defaultScale(500),
      flexDirection: 'column' as const,
    },
    iconLabelSwitchContainerLast: {
      marginBottom: isDesktopWeb ? defaultScale(-5) : null,
      width: defaultScale(340),
      alignSelf: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: defaultScale(10),
    },
  },
  [Orientations.landscape]: {
    bookingSelectionContainerHeight: defaultScale(160),
    bookingSelectionContainerWidth: `${equipmentSelectionContainerWidth}%`,
    bookingSelectionContainerMargin: {
      minWidth: defaultScale(600),
      flexDirection: 'row' as const,
      marginBottom: defaultScale(20),
      paddingHorizontal: defaultScale(15),
    },
    iconLabelSwitchContainerLast: {
      width: defaultScale(300),
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'flex-end',
      marginHorizontal: defaultScale(5),
      marginBottom: defaultScale(55),
    },
  },
};

const styles = StyleSheet.create({
  bookingSelectionContainer: {
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  headingOptionsContainer: {
    marginTop: defaultScale(20),
    flex: 1,
    justifyContent: 'center',
  },
  bookingSelectionTitle: {
    fontSize: Theme.textStyle.heading2.fontSize,
    fontFamily: Theme.textStyle.bodyCopyBold.fontFamily,
    textAlign: 'center',
  },
  bookingSelectionSubTitle: {
    fontSize: Theme.textStyle.bodyCopyMed.fontSize,
    fontFamily: Theme.textStyle.bodyCopyMed.fontFamily,
    textAlign: 'center',
  },
});

export const EquipmentSelection = connect(mapStateToProps)(
  EquipmentSelectionComponent,
);
